import React, { useEffect, useRef, useState } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * App-standard shared select: pill-shaped trigger, `border-line` border,
 * fixed to the app's 42px row-control standard (see _config/conventions.md)
 * so pages stop hand-rolling their own `<select>`/dropdown markup (see
 * QueueJobsList, UrlExplorerPage, JobPerformance, AdminSettings). Same
 * trigger/menu shape as FilterSelect but stretches to fill its container
 * (`w-full`) rather than sizing to its own label, matching how a native
 * `<select>` behaves in a form/settings layout.
 */
export const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  className = '',
  'data-testid': dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selected = options.find((option) => option.value === value);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative inline-block w-full ${className}`}>
      <div
        data-testid={dataTestId}
        role="combobox"
        aria-expanded={isOpen}
        className={`w-full inline-flex items-center justify-between gap-2 cursor-pointer rounded-full border bg-surface h-[42px] px-4 text-sm text-ink hover:border-accent/40 transition-all ${
          isOpen ? 'border-accent ring-1 ring-accent' : 'border-line'
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`truncate ${selected ? '' : 'text-ink-muted'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`w-5 h-5 flex-shrink-0 text-ink-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div
          className="absolute z-50 mt-1 w-full bg-surface border border-line rounded-lg shadow-lg"
          style={{ maxHeight: '15rem', overflowY: 'auto', overscrollBehavior: 'contain' }}
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`px-4 py-2 text-sm cursor-pointer truncate hover:bg-ghost-bg ${
                option.value === value ? 'bg-nav-active text-accent-active' : 'text-ink'
              }`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Select;
